// SlideShell — common layout for every onboarding slide.
//
// Top: progress bar + skip (where applicable).
// Body: the slide-specific content, fills the available height.
// Bottom: sticky CTA row (primary + optional secondary), safe-bottom padded.
//
// The orchestrator wraps this in framer-motion animation. The shell is
// dumb-component: it just shows the chrome.

import type { ReactNode } from 'react'

interface Props {
  /** 0..1 progress ratio for the bar at the top. */
  progress: number
  /** Optional skip handler — when provided, shows a "Sari peste" button top-right. */
  onSkip?: () => void
  /** Optional back handler — when provided, shows a back chevron top-left. */
  onBack?: () => void
  /** Slide body. */
  children: ReactNode
  /** Sticky bottom CTA row content. */
  footer?: ReactNode
}

export default function SlideShell({ progress, onSkip, onBack, children, footer }: Props) {
  return (
    <div className="fixed inset-0 z-[80] bg-white dark:bg-gray-950 flex flex-col">
      {/* Top chrome */}
      <div className="safe-top flex items-center px-4 pt-3 pb-2">
        {onBack ? (
          <button
            onClick={onBack}
            aria-label="Înapoi"
            className="w-10 h-10 -ml-2 flex items-center justify-center text-gray-500 active:opacity-60"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        ) : (
          <div className="w-10 h-10" aria-hidden="true" />
        )}
        <div className="flex-1 mx-2 h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-[width] duration-300 ease-out"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        {onSkip ? (
          <button
            onClick={onSkip}
            className="w-auto h-10 px-2 -mr-2 text-sm text-gray-500 active:opacity-60"
          >
            Sari peste
          </button>
        ) : (
          <div className="w-10 h-10" aria-hidden="true" />
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 pb-4">
        {children}
      </div>

      {/* Footer */}
      {footer ? (
        <div className="safe-bottom safe-x px-5 pt-3 pb-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950">
          {footer}
        </div>
      ) : null}
    </div>
  )
}
